<template>
  <div class="w-full h-full flex flex-col gap-5">
    <div class="w-full">
      <h3 class="text-lg font-semibold mb-4 text-center">
        Percentage Escort {{ chartTitle }}
      </h3>
      <div 
        class="w-full" 
        :style="{ height: '500px' }"
      >
        <bar-chart
          v-if="barChartData"
          :key="barChartData.labels[0]"
          class="w-full h-full"
          :data="barChartData"
          :options="barChartOptions"
          :plugins="barChartPlugins"
        />
        <div
          v-if="!barChartData"
          class="w-full h-32 flex items-center justify-center"
        >
          <span class="text-grey-001">No Data Found</span>
        </div>
      </div>
    </div>
    <div class="w-full px-5">
      <table class="w-full border-collapse border border-gray-300">
        <thead>
          <tr class="bg-gray-100">
            <th class="border border-gray-300 px-4 py-2 text-left" />
            <th
              v-for="label in chartLabels"
              :key="label"
              class="border border-gray-300 px-4 py-2 text-center"
            >
              {{ label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="border border-gray-300 px-4 py-2 font-semibold">
              <span class="inline-flex items-center gap-2">
                <span
                  class="w-3 h-3 rounded-full inline-block"
                  style="background-color: #FA1254;"
                />
                Total Accident Stolen
              </span>
            </td>
            <td
              v-for="(value, index) in totalAccidentStolen"
              :key="`accident-${index}`"
              class="border border-gray-300 px-4 py-2 text-center"
            >
              {{ value }}
            </td>
          </tr>
          <tr>
            <td class="border border-gray-300 px-4 py-2 font-semibold">
              <span class="inline-flex items-center gap-2">
                <span
                  class="w-3 h-3 rounded-full inline-block"
                  style="background-color: #7451D0;"
                />
                Total Trip
              </span>
            </td>
            <td
              v-for="(value, index) in totalTrip"
              :key="`trip-${index}`"
              class="border border-gray-300 px-4 py-2 text-center"
            >
              {{ value }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
  
<script>
import {sum, upperFirst} from "lodash";

import BarChart from "@/components/barChart";
import {tooltipConfig} from "@/utils/chartUtils";
import {handleValidateAndFormatNumber} from "@/utils/inputFormatter";
  
export default {
  props: {
    chartData: {
      type: Object,
      default: () => null
    },
    monthYear: {
      type: String,
      default: ''
    }
  },
  components: {
    BarChart
  },
  data() {
    return {
      isReady: false,
      barChartData: null
    }
  },
  computed: {
    chartTitle() {
      if (this.monthYear) {
        return this.monthYear.replace('-', ' ').toUpperCase()
      }
      return ''
    },
    processedData() {
      if (!this.chartData || !this.chartData.security_vendor_stats) {
        return {
          labels: [],
          totalTrip: [],
          totalAccidentStolen: []
        }
      }

      const vendors = this.chartData.security_vendor_stats || []
      const labels = vendors.map(v => v.security_vendor_name)
      labels.push('TOTAL')

      const totalTrip = vendors.map(v => v.total_trip || 0)
      totalTrip.push(this.chartData.total_trip || 0)

      const totalAccidentStolen = vendors.map(v => v.total_cargo_stolen || 0)
      totalAccidentStolen.push(this.chartData.total_cargo_stolen || 0)

      return {
        labels,
        totalTrip,
        totalAccidentStolen
      }
    },
    chartLabels() {
      return this.processedData.labels
    },
    totalTrip() {
      return this.processedData.totalTrip
    },
    totalAccidentStolen() {
      return this.processedData.totalAccidentStolen
    },
    yStepSize() {
      const values = [
        ...(this.processedData.totalTrip || []),
        ...(this.processedData.totalAccidentStolen || [])
      ].filter(v => typeof v === 'number' && !isNaN(v))

      if (!values.length) return 5

      const avg = values.reduce((a, b) => a + b, 0) / values.length
      const rounded = Math.max(1, Math.round(avg / 5) * 5)

      return rounded || 5
    },
    barChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        barPercentage: 0.8,
        categoryPercentage: 0.6,
        borderSkipped: false,
        borderRadius: {
          topLeft: 5,
          topRight: 5,
          bottomLeft: 5,
          bottomRight: 5
        },
        borderSkipped: 'middle',
        layout: {
          padding: {
            left: 25,
            top: 30,
            right: 25,
            bottom: 20
          }
        },
        scales: {
          x: {
            stacked: true,
            offset: true,
            beginAtZero: true,
            grid: {
              display: false
            },
            ticks: {
              color: 'black',
              font: {
                family: 'Poppins',
                size: 11
              },
              maxRotation: 0,
              minRotation: 0
            }
          },
          y: {
            beginAtZero: true,
            stacked: true,
            grid: {
              display: false
            },
            ticks: {
              stepSize: this.yStepSize,
              font: {
                family: 'Poppins',
                size: 11
              }
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          chartAreaBorder: {
            borderColor: 'black',
            borderWidth: 2
          },
          axisIcons: true,
          datalabels: {
            display: true
          },
          tooltip: tooltipConfig({
            callbacks: {
              title(titleItem) {
                const item = titleItem[0]
                const dataIndex = item?.dataIndex
                return this.barChartData?.labels[dataIndex] || ''
              },
              label(tooltipItem) {
                const datasetLabel = tooltipItem?.dataset?.label || ''
                const value = handleValidateAndFormatNumber(tooltipItem?.raw)
                return `${datasetLabel}: ${value}`
              }
            }
          })
        }
      }
    },
    barChartPlugins() {
      return [
        this.chartAreaBorderPlugin(),
        this.axisIconPlugin(),
        this.barValuePlugin()
      ]
    }
  },
  mounted() {
    this.initializeChart()
  },
  methods: {
    initializeChart() {
      if (!this.processedData.labels.length) {
        this.barChartData = null
        return
      }

      this.barChartData = {
        labels: this.processedData.labels,
        datasets: [
          {
            label: 'Total Trip',
            data: this.processedData.totalTrip,
            backgroundColor: '#7451D0', // Purple
            borderColor: '#7451D0',
            borderWidth: 0
          },
          {
            label: 'Total Accident Stolen',
            data: this.processedData.totalAccidentStolen,
            backgroundColor: '#FA1254', // Red
            borderColor: '#FA1254',
            borderWidth: 0
          }
        ]
      }
    },
    chartAreaBorderPlugin() {
      return {
        id: 'chartAreaBorder',
        beforeDraw(chart, args, options) {
          const {ctx, chartArea: {left, top, width, height}} = chart
          const extension = 15
          const padding = 1
  
          ctx.save();
          ctx.strokeStyle = options.borderColor || 'black'
          ctx.lineWidth = options.borderWidth || 2
  
          ctx.beginPath();
          ctx.moveTo(left, top - extension)
          ctx.lineTo(left, top + height + padding)
          ctx.stroke()
  
          ctx.beginPath()
          ctx.moveTo(left, top + height + padding)
          ctx.lineTo(left + width + extension, top + height + padding)
          ctx.stroke();
  
          ctx.restore()
        }
      }
    },
    axisIconPlugin() {
      return {
        id: 'axisIcons',
        beforeDraw(chart, args, options) {
          const { ctx, chartArea: { top, left, right, bottom }, scales } = chart;

          const topIcon = {
            text: '\u2BC5',
            x: left,
            y: top - 15,
            color: '#000',
            font: 'bold 20px FontAwesome'
          };

          const rightIcon = {
            text: '\u2BC8',
            x: right + 15,
            y: bottom + 2,
            color: '#000',
            font: 'bold 20px Poppins'
          }

          ctx.save()
          ctx.font = topIcon.font
          ctx.fillStyle = topIcon.color
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(topIcon.text, topIcon.x, topIcon.y)
          ctx.restore();

          ctx.save()
          ctx.font = rightIcon.font
          ctx.fillStyle = rightIcon.color
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(rightIcon.text, rightIcon.x, rightIcon.y)
          ctx.restore()
        }
      }
    },
    barValuePlugin() {
      return {
        id: 'barValue',
        afterDatasetsDraw(chart) {
          const { ctx, chartArea: { top, bottom }, scales } = chart;
          const datasets = chart.data.datasets;
          
          datasets.forEach((dataset, datasetIndex) => {
            const meta = chart.getDatasetMeta(datasetIndex);
            
            meta.data.forEach((bar, index) => {
              const value = dataset.data[index];
              
              if (value !== null && value !== undefined && value !== 0) {
                const x = bar.x;
                let y = bar.y;
                
                // For stacked bars, position text differently
                if (datasetIndex === 0) {
                  // First dataset (Total Trip) - show value in the middle of the bar
                  y = bar.y + (bar.height / 2);
                } else {
                  // Second dataset (Total Accident Stolen) - show value above the bar
                  y = bar.y - 10;
                }
                
                ctx.save();
                ctx.fillStyle = '#000';
                ctx.font = '600 12px Poppins';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(value.toString(), x, y);
                ctx.restore();
              }
            });
          });
        }
      }
    }
  },
  watch: {
    chartData: {
      immediate: true,
      deep: true,
      handler() {
        this.initializeChart()
      }
    },
    processedData: {
      deep: true,
      handler() {
        this.initializeChart()
      }
    }
  }
}
</script>
  
  <style lang="scss" scoped>
  </style>